import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { PortfolioApi } from 'API';
import { titleCase } from 'utils';
import {
    FormCheckbox,
    FormInput,
    FormPostalCodeInput,
    FormSection,
    getFieldName,
    StateSelector,
    VerticalLayout,
} from 'widgets';

export const AddressBlock = ({ prefix }: { prefix?: string }) => {
    return (
        <>
            <PrimaryAddressFormSection prefix={prefix} hasDivider />

            <MailingAddressFormSection prefix={prefix} hasDivider />

            <BillingAddressFormSection prefix={prefix} hasDivider />
        </>
    );
};

interface FormSectionProps {
    title?: string;
    className?: string;
    prefix?: string;
    hasDivider?: boolean;
}

export const PrimaryAddressFormSection = ({
    title = 'Primary Address',
    className,
    prefix,
    hasDivider,
}: FormSectionProps) => {
    return (
        <FormSection title={title} hasDivider={hasDivider} className={className}>
            <AddressFields prefix={getFieldName('primaryAddress', prefix)} />
        </FormSection>
    );
};

export const MailingAddressFormSection = ({
    title = 'Mailing Address',
    className,
    prefix,
    hasDivider,
}: FormSectionProps) => {
    const { watch } = useFormContext();
    const mailingAddress = watch(getFieldName('mailingAddress', prefix));
    return (
        <FormSection title={title} hasDivider={hasDivider} className={className}>
            <FormCheckbox name={getFieldName('mailingAddress.sameAsPrimary', prefix)} label="Same as primary" />
            {!mailingAddress?.sameAsPrimary && <AddressFields prefix={getFieldName('mailingAddress', prefix)} />}
        </FormSection>
    );
};

export const BillingAddressFormSection = ({
    title = 'Billing Address',
    className,
    prefix,
    hasDivider,
}: FormSectionProps) => {
    const { watch } = useFormContext();
    const billingAddress = watch(getFieldName('billingAddress', prefix));
    return (
        <FormSection title={title} hasDivider={hasDivider} className={className}>
            <FormCheckbox name={getFieldName('billingAddress.sameAsPrimary', prefix)} label="Same as primary" />
            {!billingAddress?.sameAsPrimary && <AddressFields prefix={getFieldName('billingAddress', prefix)} />}
        </FormSection>
    );
};

const AddressFields = ({ prefix }: { prefix: string }) => {
    const { watch, setValue, setError, clearErrors } = useFormContext();

    const postalCodeFieldName = `${prefix}.postalCode`;
    const postalCode = watch(postalCodeFieldName);

    const onPostalCodeBlur = useCallback(async () => {
        if (postalCode && postalCode.length !== 5) {
            setError(postalCodeFieldName, {
                message: 'Invalid postal code',
            });
            return;
        }

        const [response] = await PortfolioApi().getLocaleFromZipCode(postalCode);

        if (response) {
            setValue(`${prefix}.city`, titleCase(response.city), {
                shouldDirty: true,
            });
            setValue(`${prefix}.state`, response.state, {
                shouldDirty: true,
            });
            clearErrors(postalCodeFieldName);
        } else {
            setError(postalCodeFieldName, {
                message: 'Unknown US postal code',
            });
        }
    }, [clearErrors, postalCode, postalCodeFieldName, prefix, setError, setValue]);

    return (
        <VerticalLayout $gap={4}>
            <FormInput label="Street Address" name={`${prefix}.street`} placeholder="24 Federal St." />
            <FormInput label="Address Line 2" name={`${prefix}.street2`} placeholder="Optional" optional />
            <FormPostalCodeInput label="Postal Code" name={postalCodeFieldName} onBlur={onPostalCodeBlur} />
            <FormInput label="City" name={`${prefix}.city`} placeholder="Boston" />
            <StateSelector label="State" name={`${prefix}.state`} />
        </VerticalLayout>
    );
};
